import { FeatureFlagService } from 'features/feature-toggle/services';
import { AST_SIGNMAIL_MODE_ENABLED } from 'features/ast-signmail/constants';
import { GreetingPreference } from 'features/signmail/enums';

export interface AstSignMailFlag {
  enabled?: boolean;
  emergencyCallbacksEnabled?: boolean;
  greetings?: string[];
  allowedLanguages?: string[];
  initialCallOnly?: boolean;
}

export interface AstSignMailRoutingContext {
  isDeafInitialConnection: boolean;
  isDeafReconnect: boolean;
  isSpawnedCallFailure: boolean;
}

export interface AstSignMailEligibility {
  astSignmailFlag: AstSignMailFlag | undefined;
  greetingPreferenceKey: string | undefined;
  isGreetingAllowed: boolean;
  isLanguageAllowed: boolean;
  isAstSignMailModeEnabled: boolean;
  isInitialCallOnlyPolicyEnabled: boolean;
  isAllowedByInitialCallOnlyPolicy: boolean;
  isAstEligible: boolean;
  routingDetails: string;
}

const CANONICAL_LANGUAGE_CODE_BY_VALUE: Record<string, string> = {
  en: 'en',
  english: 'en',
  es: 'es',
  spanish: 'es',
};

export const normalizeGreetingPreference = (
  greetingPreference: string | number | undefined
): GreetingPreference | undefined => {
  if (greetingPreference === undefined || greetingPreference === null) {
    return undefined;
  }

  const asString = String(greetingPreference);

  if (
    Object.values(GreetingPreference).includes(asString as GreetingPreference)
  ) {
    return asString as GreetingPreference;
  }

  if (asString in GreetingPreference) {
    return GreetingPreference[asString as keyof typeof GreetingPreference];
  }

  return undefined;
};

export const resolveGreetingPreferenceFlagKey = (
  greetingPreference: GreetingPreference
): string | undefined => {
  return Object.entries(GreetingPreference).find(
    ([, value]) => value === greetingPreference
  )?.[0];
};

export const normalizeAstSignMailLanguage = (
  language: string | undefined
): string | undefined => {
  if (!language) {
    return undefined;
  }

  const normalizedLanguage = language.trim().toLowerCase();

  if (!normalizedLanguage) {
    return undefined;
  }

  return (
    CANONICAL_LANGUAGE_CODE_BY_VALUE[normalizedLanguage] ?? normalizedLanguage
  );
};

const isGreetingPreferenceAllowed = (
  greetingPreference: GreetingPreference,
  astSignmailFlag: AstSignMailFlag | undefined
) => {
  // LD stores greeting allow-list keys like "SORENSON_GREETING", not enum values like "1".
  const greetingPreferenceKey =
    resolveGreetingPreferenceFlagKey(greetingPreference);

  return {
    greetingPreferenceKey,
    isGreetingAllowed:
      !!greetingPreferenceKey &&
      !!astSignmailFlag?.greetings?.includes(greetingPreferenceKey),
  };
};

const isCallLanguageAllowed = (
  callLanguageCode: string,
  astSignmailFlag: AstSignMailFlag | undefined
) => {
  // Normalize both sides so "English"/"en" and "Spanish"/"es" compare equal.
  const normalizedCallLanguage = normalizeAstSignMailLanguage(callLanguageCode);
  const normalizedAllowedLanguages =
    astSignmailFlag?.allowedLanguages
      ?.map(normalizeAstSignMailLanguage)
      .filter((language): language is string => !!language) ?? [];

  return {
    normalizedCallLanguage,
    normalizedAllowedLanguages,
    isLanguageAllowed:
      !!normalizedCallLanguage &&
      normalizedAllowedLanguages.includes(normalizedCallLanguage),
  };
};

/**
 * Evaluates whether auto AST SignMail should run for a call.
 *
 * Eligible only when all four are true:
 * 1. The LaunchDarkly `astSignmail` flag is enabled
 * 2. The deaf user's greeting preference is in the flag's allow-list
 * 3. The call language is in the flag's allow-list
 * 4. Core returned ASTSignMailMode === enabled
 *
 * Returns both the final decision and the intermediate values so callers
 * (orchestrator analytics, debug menu) can explain why routing passed or failed.
 */
export const resolveAstSignMailEligibility = (
  greetingPreference: GreetingPreference,
  callLanguageCode: string,
  astSignMailMode: number | undefined,
  routingContext?: AstSignMailRoutingContext
) => {
  const astSignmailFlag = FeatureFlagService.getInstance().flags.astSignmail as
    | AstSignMailFlag
    | undefined;

  const { greetingPreferenceKey, isGreetingAllowed } =
    isGreetingPreferenceAllowed(greetingPreference, astSignmailFlag);

  const {
    normalizedCallLanguage,
    normalizedAllowedLanguages,
    isLanguageAllowed,
  } = isCallLanguageAllowed(callLanguageCode, astSignmailFlag);

  const isAstSignMailModeEnabled =
    astSignMailMode === AST_SIGNMAIL_MODE_ENABLED;

  const isInitialCallOnlyPolicyEnabled =
    astSignmailFlag?.initialCallOnly ?? false;

  const isAllowedByInitialCallOnlyPolicy =
    !isInitialCallOnlyPolicyEnabled ||
    !routingContext ||
    routingContext.isSpawnedCallFailure ||
    (routingContext.isDeafInitialConnection && !routingContext.isDeafReconnect);

  const isAstEligible =
    (astSignmailFlag?.enabled ?? false) &&
    isGreetingAllowed &&
    isLanguageAllowed &&
    isAstSignMailModeEnabled &&
    isAllowedByInitialCallOnlyPolicy;

  const routingDetails = [
    `greetingPreference=${greetingPreference}`,
    `greetingPreferenceKey=${greetingPreferenceKey ?? 'unknown'}`,
    `callLanguageCode=${callLanguageCode || 'unknown'}`,
    `normalizedCallLanguage=${normalizedCallLanguage ?? 'unknown'}`,
    `allowedLanguages=${JSON.stringify(astSignmailFlag?.allowedLanguages ?? [])}`,
    `normalizedAllowedLanguages=${JSON.stringify(normalizedAllowedLanguages)}`,
    `isLanguageAllowed=${isLanguageAllowed}`,
    `isAstSignMailModeEnabled=${isAstSignMailModeEnabled}`,
    `isInitialCallOnlyPolicyEnabled=${isInitialCallOnlyPolicyEnabled}`,
    `isAllowedByInitialCallOnlyPolicy=${isAllowedByInitialCallOnlyPolicy}`,
    `astSignMailMode=${astSignMailMode ?? 'unknown'}`,
    `astSignmailFlag=${JSON.stringify(astSignmailFlag ?? {})}`,
    `isAstEligible=${isAstEligible}`,
  ].join(', ');

  return {
    astSignmailFlag,
    greetingPreferenceKey,
    isGreetingAllowed,
    isLanguageAllowed,
    isAstSignMailModeEnabled,
    isInitialCallOnlyPolicyEnabled,
    isAllowedByInitialCallOnlyPolicy,
    routingDetails,
    isAstEligible,
  };
};
